import {
  Box,
  Button,
  Center,
  Circle,
  IconButton,
  Link,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Stack,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import * as React from 'react';
import FocusLock from 'react-focus-lock';

import { useCurrent } from '@/hooks/useCurrent';
import { Loading } from '@/imports/ui/chakra/components/Loading';
import { BellIcon } from '@/imports/ui/chakra/feather';
import { useIsMobile } from '@/imports/ui/responsive/Responsive';
import {
  useWebflowUtilitiesLifecycle,
  WEBFLOW_NOTIFICATIONS_HOLD_KEY,
} from '@/lib/webflow-poc/webflow-utilities-lifecycle';

import {
  IStreamNotificationFeed,
  useStreamFeedContext,
} from '../../StreamFeedProvider';
import {
  IUnifiedNotificationItem,
  mergeNotificationItems,
} from '../../hooks/mergeNotificationItems';
import { useInAppSystemNotifications } from '../../hooks/useInAppSystemNotifications';
import { useNotificationFeed } from '../../hooks/useNotificationFeed';
import { InAppSystemNotificationItem } from '../InAppSystemNotificationItem';
import { InAppNotificationDropdownItem } from './InAppNotificationDropdownItem';

const STREAM_FEED_LIMIT = 8;
const SYSTEM_NOTIFICATION_LIMIT = 20;

const NotificationList: React.FC<{
  items: IUnifiedNotificationItem[];
  loading: boolean;
  hasMore: boolean;
  newUpdates?: boolean;
  onSeeNew?: () => void;
  markSystemRead: (notificationId: string) => Promise<void>;
}> = ({ items, loading, hasMore, newUpdates, onSeeNew, markSystemRead }) => {
  return (
    <>
      {newUpdates && onSeeNew && (
        <Box
          position="absolute"
          width="100%"
          display="flex"
          placeContent="center"
        >
          <Button borderRadius="full" mt="8" onClick={onSeeNew}>
            See New Notifications
          </Button>
        </Box>
      )}
      {items.length > 0 ? (
        <Stack>
          {items.map((item) =>
            item.kind === 'system' ? (
              <InAppSystemNotificationItem
                key={item.id}
                notification={item.notification}
                markRead={markSystemRead}
              />
            ) : (
              <InAppNotificationDropdownItem
                key={item.id}
                enrichedActivityGroup={item.streamItem.enrichedActivityGroup}
                markRead={item.streamItem.markRead}
              />
            )
          )}
        </Stack>
      ) : loading ? (
        <Loading />
      ) : (
        <Box py="4" px="16" role="menuitem">
          There {`aren't`} any notifications for you yet.
        </Box>
      )}
      {loading && items.length > 0 && <Loading />}
      {hasMore && !loading && (
        <Center p="4">
          <Link as={NextLink} href={'/notifications'}>
            View More
          </Link>
        </Center>
      )}
    </>
  );
};

const NotificationBell: React.FC<{
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  unseenCount: number;
  children: React.ReactNode;
  /** Raise popover above Webflow mobile nav / utilities portal. */
  elevatedZIndex?: boolean;
}> = ({ isOpen, onOpen, onClose, unseenCount, children, elevatedZIndex }) => {
  return (
    <Box>
      <Popover
        isOpen={isOpen}
        onClose={onClose}
        isLazy
        placement="bottom-end"
        gutter={8}
      >
        <Box position="relative">
          <PopoverTrigger>
            <IconButton
              onClick={onOpen}
              aria-label="Notifications"
              icon={<BellIcon />}
              variant="outline"
              colorScheme="gray"
              isRound
            />
          </PopoverTrigger>
          {unseenCount > 0 ? (
            <Circle
              position="absolute"
              top={-1}
              right={-1}
              size={4}
              backgroundColor="red.600"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Text color="white" fontSize={10} lineHeight="1em">
                {unseenCount > 9 ? '9+' : unseenCount}
              </Text>
            </Circle>
          ) : null}
        </Box>
        <PopoverContent
          maxWidth="440px"
          w="min(440px, calc(100vw - 24px))"
          maxHeight="80vh"
          overflowY="auto"
          borderRadius="xl"
          // Above Webflow utilities (~10050) when opened from hybrid nav.
          zIndex={elevatedZIndex ? 11000 : undefined}
        >
          <FocusLock returnFocus persistentFocus={false}>
            <PopoverBody>
              <Text textStyle="xlBodyStrong" p="2">
                Notifications
              </Text>
              {children}
            </PopoverBody>
          </FocusLock>
        </PopoverContent>
      </Popover>
    </Box>
  );
};

const DropdownWithStream: React.FC<{
  notificationFeed: IStreamNotificationFeed;
  tenantId: string;
}> = ({ notificationFeed, tenantId }) => {
  const router = useRouter();
  const isMobile = useIsMobile();
  // Present only under Webflow utilities — show popover instead of /notifications.
  const webflowUtilitiesLifecycle = useWebflowUtilitiesLifecycle();
  const preferPopover = Boolean(webflowUtilitiesLifecycle);
  const {
    isOpen,
    onOpen: defaultOnOpen,
    onClose: defaultOnClose,
  } = useDisclosure();

  const {
    loading,
    notificationItems,
    notificationsMetadata,
    resetNotifications,
    paginationOptions,
    newUpdates,
    clearNewUpdates,
    markNotificationsAsSeen,
  } = useNotificationFeed({
    tenantId,
    notificationFeed,
    notificationLimit: STREAM_FEED_LIMIT,
    shouldRefetchNotificationsOnUpdate: !isOpen,
  });

  const {
    notifications: systemNotifications,
    unseenCount: systemUnseen,
    loading: systemLoading,
    hasMore: systemHasMore,
    markRead: markSystemRead,
    markAllSeen: markSystemSeen,
  } = useInAppSystemNotifications({
    limit: SYSTEM_NOTIFICATION_LIMIT,
  });

  const onClose = React.useCallback(() => {
    defaultOnClose();
    webflowUtilitiesLifecycle?.releaseHold(WEBFLOW_NOTIFICATIONS_HOLD_KEY);
    if (newUpdates) {
      clearNewUpdates();
      resetNotifications();
    }
  }, [
    defaultOnClose,
    clearNewUpdates,
    resetNotifications,
    newUpdates,
    webflowUtilitiesLifecycle,
  ]);

  const onOpen = React.useCallback(() => {
    // Classic mobile TW header still goes to the full page; Webflow hybrid keeps the popup.
    if (isMobile && !preferPopover) {
      router.push('/notifications');
      return;
    }
    webflowUtilitiesLifecycle?.holdOpen(WEBFLOW_NOTIFICATIONS_HOLD_KEY);
    defaultOnOpen();
    markNotificationsAsSeen();
    markSystemSeen();
  }, [
    defaultOnOpen,
    markNotificationsAsSeen,
    markSystemSeen,
    isMobile,
    preferPopover,
    router,
    webflowUtilitiesLifecycle,
  ]);

  const unifiedItems = React.useMemo(
    () =>
      mergeNotificationItems({
        streamItems: notificationItems,
        systemNotifications,
      }),
    [notificationItems, systemNotifications]
  );

  return (
    <NotificationBell
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
      unseenCount={(notificationsMetadata.numUnseen || 0) + systemUnseen}
      elevatedZIndex={preferPopover}
    >
      <NotificationList
        items={unifiedItems}
        loading={loading || systemLoading}
        hasMore={paginationOptions.hasNext || systemHasMore}
        newUpdates={newUpdates}
        onSeeNew={() => resetNotifications(true)}
        markSystemRead={markSystemRead}
      />
    </NotificationBell>
  );
};

const DropdownSystemOnly: React.FC = () => {
  const router = useRouter();
  const isMobile = useIsMobile();
  const webflowUtilitiesLifecycle = useWebflowUtilitiesLifecycle();
  const preferPopover = Boolean(webflowUtilitiesLifecycle);
  const {
    isOpen,
    onOpen: defaultOnOpen,
    onClose: defaultOnClose,
  } = useDisclosure();

  const {
    notifications,
    unseenCount,
    loading,
    hasMore,
    markRead,
    markAllSeen,
  } = useInAppSystemNotifications({
    limit: SYSTEM_NOTIFICATION_LIMIT,
  });

  const onClose = React.useCallback(() => {
    defaultOnClose();
    webflowUtilitiesLifecycle?.releaseHold(WEBFLOW_NOTIFICATIONS_HOLD_KEY);
  }, [defaultOnClose, webflowUtilitiesLifecycle]);

  const onOpen = React.useCallback(() => {
    if (isMobile && !preferPopover) {
      router.push('/notifications');
      return;
    }
    webflowUtilitiesLifecycle?.holdOpen(WEBFLOW_NOTIFICATIONS_HOLD_KEY);
    defaultOnOpen();
    markAllSeen();
  }, [
    defaultOnOpen,
    markAllSeen,
    isMobile,
    preferPopover,
    router,
    webflowUtilitiesLifecycle,
  ]);

  const unifiedItems = React.useMemo(
    () =>
      mergeNotificationItems({
        streamItems: [],
        systemNotifications: notifications,
      }),
    [notifications]
  );

  return (
    <NotificationBell
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
      unseenCount={unseenCount}
      elevatedZIndex={preferPopover}
    >
      <NotificationList
        items={unifiedItems}
        loading={loading}
        hasMore={hasMore}
        markSystemRead={markRead}
      />
    </NotificationBell>
  );
};

export const InAppNotificationDropdown: React.FC = () => {
  const streamFeedState = useStreamFeedContext();
  const current = useCurrent();
  const router = useRouter();
  const notificationsActive = router.pathname === '/notifications';

  if (notificationsActive) {
    return (
      <IconButton
        aria-label="Notifications"
        icon={<BellIcon />}
        variant="outline"
        colorScheme="gray"
        isRound
        disabled
      />
    );
  }

  if (!current.tenant?._id) {
    return null;
  }

  return streamFeedState?.notificationFeed ? (
    <DropdownWithStream
      notificationFeed={streamFeedState.notificationFeed}
      tenantId={current.tenant._id}
    />
  ) : (
    <DropdownSystemOnly />
  );
};
